import { Controller } from "@hotwired/stimulus"

export default class extends Controller {
  static values = {
    type: String,
    data: Object
  }

  async connect() {
    // Load Chart.js if not already loaded
    if (typeof Chart === 'undefined') {
      await this.loadChartJs()
    }

    this.renderChart()
  }

  loadChartJs() {
    return new Promise((resolve) => {
      const script = document.createElement('script')
      script.src = 'https://cdn.jsdelivr.net/npm/chart.js'
      script.onload = resolve
      document.head.appendChild(script)
    })
  }

  renderChart() {
    const ctx = this.element.getContext('2d')

    // Common chart options for dark theme
    const commonOptions = {
      responsive: true,
      maintainAspectRatio: false,
      plugins: {
        legend: {
          labels: {
            color: '#a1a1aa',
            font: {
              family: 'Inter, system-ui, sans-serif'
            }
          }
        }
      }
    }

    if (this.typeValue === 'doughnut') {
      this.createDoughnutChart(ctx, commonOptions)
    } else if (this.typeValue === 'line') {
      this.createLineChart(ctx, commonOptions)
    }
  }

  // Color palette for category slices. Unmatched/fallback categories use gray;
  // matched categories cycle through the accent palette.
  categoryColors(categories) {
    const palette = [
      'rgba(34, 197, 94, 0.8)',   // green
      'rgba(168, 85, 247, 0.8)',  // purple
      'rgba(59, 130, 246, 0.8)',  // blue
      'rgba(234, 179, 8, 0.8)',   // amber
      'rgba(236, 72, 153, 0.8)',  // pink
      'rgba(20, 184, 166, 0.8)'   // teal
    ]
    const gray = 'rgba(113, 113, 122, 0.6)'
    let accent = 0
    return categories.map((c) => (c.skip ? gray : palette[accent++ % palette.length]))
  }

  createDoughnutChart(ctx, commonOptions) {
    // Prefer the full per-category breakdown when the server provides it;
    // otherwise fall back to the legacy Important/Unmatched split.
    const categories = Array.isArray(this.dataValue.categories) ? this.dataValue.categories : null

    const labels = categories
      ? categories.map((c) => c.label)
      : ['Important', 'Unmatched']
    const values = categories
      ? categories.map((c) => c.count)
      : [this.dataValue.important || 0, this.dataValue.skipped || 0]
    const colors = categories
      ? this.categoryColors(categories)
      : ['rgba(34, 197, 94, 0.8)', 'rgba(113, 113, 122, 0.6)']

    this.chart = new Chart(ctx, {
      type: 'doughnut',
      data: {
        labels: labels,
        datasets: [{
          data: values,
          backgroundColor: colors,
          borderColor: colors.map((c) => c.replace(/0\.\d+\)$/, '1)')),
          borderWidth: 1,
          hoverOffset: 4
        }]
      },
      options: {
        ...commonOptions,
        cutout: '65%',
        plugins: {
          ...commonOptions.plugins,
          legend: {
            position: 'bottom',
            labels: {
              color: '#a1a1aa',
              padding: 15,
              usePointStyle: true,
              font: {
                family: 'Inter, system-ui, sans-serif',
                size: 12
              }
            }
          }
        }
      }
    })
  }

  createLineChart(ctx, commonOptions) {
    this.chart = new Chart(ctx, {
      type: 'line',
      data: {
        labels: this.dataValue.labels || [],
        datasets: [
          {
            label: 'Matched',
            data: this.dataValue.important || [],
            borderColor: 'rgba(34, 197, 94, 1)',
            backgroundColor: 'rgba(34, 197, 94, 0.1)',
            fill: true,
            tension: 0.3,
            pointRadius: 4,
            pointBackgroundColor: 'rgba(34, 197, 94, 1)'
          },
          {
            label: 'Fallback',
            data: this.dataValue.skipped || [],
            borderColor: 'rgba(113, 113, 122, 1)',
            backgroundColor: 'rgba(113, 113, 122, 0.1)',
            fill: true,
            tension: 0.3,
            pointRadius: 4,
            pointBackgroundColor: 'rgba(113, 113, 122, 1)'
          }
        ]
      },
      options: {
        ...commonOptions,
        scales: {
          x: {
            grid: {
              color: 'rgba(113, 113, 122, 0.1)'
            },
            ticks: {
              color: '#71717a',
              font: {
                family: 'Inter, system-ui, sans-serif',
                size: 11
              }
            }
          },
          y: {
            beginAtZero: true,
            grid: {
              color: 'rgba(113, 113, 122, 0.1)'
            },
            ticks: {
              color: '#71717a',
              stepSize: 1,
              font: {
                family: 'Inter, system-ui, sans-serif',
                size: 11
              }
            }
          }
        },
        plugins: {
          ...commonOptions.plugins,
          legend: {
            position: 'top',
            align: 'end',
            labels: {
              color: '#a1a1aa',
              usePointStyle: true,
              boxWidth: 8,
              padding: 15,
              font: {
                family: 'Inter, system-ui, sans-serif',
                size: 12
              }
            }
          }
        }
      }
    })
  }

  disconnect() {
    // Cleanup chart if needed
    if (this.chart) {
      this.chart.destroy()
    }
  }
}
